import { createTheme, ThemeOptions } from "@mui/material/styles";
import { alpha } from "@mui/material";

// Modern Violet/Indigo Palette
const lightPalette = {
  primary: {
    main: "#6366f1", // Indigo 500
    light: "#818cf8", // Indigo 400
    dark: "#4f46e5", // Indigo 600
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#8b5cf6", // Violet 500
    light: "#a78bfa", // Violet 400
    dark: "#7c3aed", // Violet 600
    contrastText: "#ffffff",
  },
  success: {
    main: "#10b981", // Emerald 500
    light: "#34d399",
    dark: "#059669",
  },
  warning: {
    main: "#f59e0b", // Amber 500
    light: "#fbbf24",
    dark: "#d97706",
  },
  error: {
    main: "#ef4444", // Red 500
    light: "#f87171",
    dark: "#dc2626",
  },
  info: {
    main: "#3b82f6", // Blue 500
    light: "#60a5fa",
    dark: "#2563eb",
  },
  background: {
    default: "#f8fafc", // Slate 50
    paper: "#ffffff",
  },
  text: {
    primary: "#1e293b", // Slate 800
    secondary: "#64748b", // Slate 500
    disabled: "#94a3b8", // Slate 400
  },
};

const darkPalette = {
  primary: {
    main: "#818cf8", // Indigo 400
    light: "#a5b4fc", // Indigo 300
    dark: "#6366f1", // Indigo 500
    contrastText: "#0f172a",
  },
  secondary: {
    main: "#a78bfa", // Violet 400
    light: "#c4b5fd", // Violet 300
    dark: "#8b5cf6", // Violet 500
    contrastText: "#0f172a",
  },
  success: {
    main: "#34d399", // Emerald 400
    light: "#6ee7b7",
    dark: "#10b981",
  },
  warning: {
    main: "#fbbf24", // Amber 400
    light: "#fcd34d",
    dark: "#f59e0b",
  },
  error: {
    main: "#f87171", // Red 400
    light: "#fca5a5",
    dark: "#ef4444",
  },
  info: {
    main: "#60a5fa", // Blue 400
    light: "#93c5fd",
    dark: "#3b82f6",
  },
  background: {
    default: "#0f172a", // Slate 900
    paper: "#1e293b", // Slate 800
  },
  text: {
    primary: "#f1f5f9", // Slate 100
    secondary: "#94a3b8", // Slate 400
    disabled: "#64748b", // Slate 500
  },
};

// Common theme options
const commonThemeOptions: ThemeOptions = {
  typography: {
    fontFamily: "'Inter', sans-serif",
    h1: {
      fontSize: "3rem",
      fontWeight: 800,
      letterSpacing: "-0.025em",
      lineHeight: 1.2,
    },
    h2: {
      fontSize: "2.25rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      lineHeight: 1.3,
    },
    h3: {
      fontSize: "1.875rem",
      fontWeight: 700,
      letterSpacing: "-0.025em",
      lineHeight: 1.3,
    },
    h4: {
      fontSize: "1.5rem",
      fontWeight: 600,
      letterSpacing: "-0.025em",
      lineHeight: 1.4,
    },
    h5: {
      fontSize: "1.25rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      lineHeight: 1.4,
    },
    h6: {
      fontSize: "1rem",
      fontWeight: 600,
      letterSpacing: "-0.015em",
      lineHeight: 1.4,
    },
    body1: {
      fontSize: "1rem",
      lineHeight: 1.6,
      letterSpacing: "0.005em",
    },
    body2: {
      fontSize: "0.875rem",
      lineHeight: 1.6,
      letterSpacing: "0.005em",
    },
    button: {
      textTransform: "none",
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
  },
  shape: {
    borderRadius: 16,
  },
  spacing: 8,
};

// Create light theme
export const createLightTheme = () => {
  return createTheme({
    ...commonThemeOptions,
    palette: {
      mode: "light",
      ...lightPalette,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "10px 24px",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-1px)",
            },
          },
          contained: {
            boxShadow: `0 4px 6px -1px ${alpha(lightPalette.primary.main, 0.2)}, 0 2px 4px -1px ${alpha(lightPalette.primary.main, 0.1)}`,
            // "&:hover": {
            //   boxShadow: `0 10px 15px -3px ${alpha(lightPalette.primary.main, 0.3)}, 0 4px 6px -2px ${alpha(lightPalette.primary.main, 0.15)}`,
            // },
          },
          outlined: {
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${alpha("#000000", 0.05)}`,
            boxShadow: `0 4px 6px -1px ${alpha("#000000", 0.05)}, 0 2px 4px -1px ${alpha("#000000", 0.03)}`,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-4px)",
              boxShadow: `0 20px 25px -5px ${alpha("#000000", 0.1)}, 0 10px 10px -5px ${alpha("#000000", 0.04)}`,
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              transition: "all 0.2s ease-in-out",
              "& fieldset": {
                borderColor: "#e2e8f0",
              },
              "&:hover fieldset": {
                borderColor: "#cbd5e1",
              },
              "&.Mui-focused fieldset": {
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha("#ffffff", 0.8),
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${alpha("#000000", 0.05)}`,
            boxShadow: "none",
            color: "#1e293b",
          },
        },
      },
    },
  });
};

// Create dark theme
export const createDarkTheme = () => {
  return createTheme({
    ...commonThemeOptions,
    palette: {
      mode: "dark",
      ...darkPalette,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            padding: "10px 24px",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateY(-1px)",
            },
          },
          contained: {
            boxShadow: `0 4px 6px -1px ${alpha(darkPalette.primary.main, 0.3)}, 0 2px 4px -1px ${alpha(darkPalette.primary.main, 0.2)}`,
            // "&:hover": {
            //   boxShadow: `0 10px 15px -3px ${alpha(darkPalette.primary.main, 0.4)}, 0 4px 6px -2px ${alpha(darkPalette.primary.main, 0.25)}`,
            // },
          },
          outlined: {
            borderWidth: 2,
            "&:hover": {
              borderWidth: 2,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            border: `1px solid ${alpha("#ffffff", 0.1)}`,
            backgroundColor: alpha("#1e293b", 0.6),
            backdropFilter: "blur(12px)",
            boxShadow: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            // "&:hover": {
            //   transform: "translateY(-4px)",
            //   backgroundColor: alpha("#1e293b", 0.8),
            //   boxShadow: `0 20px 25px -5px ${alpha("#000000", 0.3)}, 0 10px 10px -5px ${alpha("#000000", 0.2)}`,
            //   borderColor: alpha(darkPalette.primary.main, 0.5),
            // },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 12,
              backgroundColor: alpha("#0f172a", 0.4),
              "& fieldset": {
                borderColor: alpha("#ffffff", 0.1),
              },
              "&:hover fieldset": {
                borderColor: alpha("#ffffff", 0.2),
              },
              "&.Mui-focused fieldset": {
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: alpha("#0f172a", 0.8),
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${alpha("#ffffff", 0.1)}`,
            boxShadow: "none",
          },
        },
      },
    },
  });
};
